import React from 'react';
import { Play, Flag, Timer } from 'lucide-react';
import VideoPlayer from '../components/VideoPlayer/VideoPlayer';
import FrameDisplay from '../components/FrameDisplay';
import BackToDashboard from '../components/BackToDashboard';
import { useContactAnalysisStore } from '../stores/contactAnalysisStore';
import { useSpeedAnalysisStore } from '../stores/speedAnalysisStore';
import { useVideoStore } from '../stores/videoStore';
import { cn } from '../utils/cn';

const ContactPage = () => {
  const {
    startFrame,
    endFrame,
    fps,
    analysisType,
    contactTime,
    flightTime,
    setStartFrame,
    setEndFrame,
    setFps,
    setAnalysisType,
    calculateAndSetTime,
    reset,
  } = useContactAnalysisStore();

  const speedFps = useSpeedAnalysisStore(state => state.fps);
  const { currentFrame } = useVideoStore();

  // Sync fps with speed analysis store
  React.useEffect(() => {
    setFps(speedFps);
  }, [speedFps, setFps]);

  const handleStartFrame = () => {
    setStartFrame(currentFrame);
  };

  const handleEndFrame = () => {
    setEndFrame(currentFrame);
  };

  const handleAnalysis = (type: 'contact' | 'flight') => {
    setAnalysisType(type);
    calculateAndSetTime();
  };

  const getFeedbackMessage = () => {
    const messages = [];
    
    if (contactTime && contactTime >= 0.15) {
      messages.push("接地時間がやや長いです、腰が落ちないよう、重心を高く保ちましょう");
    }
    
    if (flightTime && flightTime < 0.1) {
      messages.push("滞空時間がやや短いです、弾むように走ってみましょう");
    }

    // Calculate contact/flight time ratio if both times are available
    if (contactTime && flightTime) {
      const totalTime = contactTime + flightTime;
      const contactRatio = (contactTime / totalTime) * 100;
      const flightRatio = (flightTime / totalTime) * 100;

      if (contactRatio >= 47 && contactRatio <= 53 && flightRatio >= 47 && flightRatio <= 53) {
        messages.push("接地時間と滞空時間のバランスに問題はありません");
      }
    }

    return messages;
  };

  const feedbackMessages = getFeedbackMessage();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
      <h1 className="text-3xl font-bold text-gray-900">接地/滞空分析</h1>
      
      <div className="mt-6 space-y-6">
        <VideoPlayer />
        
        <div className="bg-white p-6 rounded-lg shadow space-y-6">
          <div className="flex items-center justify-center space-x-4">
            <button
              onClick={handleStartFrame}
              className={cn(
                "btn btn-primary space-x-2",
                startFrame !== null && "bg-green-600 hover:bg-green-700"
              )}
            >
              <Play className="w-4 h-4" />
              <span>開始フレーム {startFrame !== null ? `(${startFrame})` : ''}</span>
            </button>
            
            <button
              onClick={handleEndFrame}
              className={cn(
                "btn btn-primary space-x-2",
                endFrame !== null && "bg-red-600 hover:bg-red-700"
              )}
            >
              <Flag className="w-4 h-4" />
              <span>終了フレーム {endFrame !== null ? `(${endFrame})` : ''}</span>
            </button>

            <button
              onClick={reset}
              className="btn btn-secondary"
            >
              リセット
            </button>
          </div>

          <FrameDisplay
            startFrame={startFrame}
            endFrame={endFrame}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700">
              フレームレート (fps)
            </label>
            <div className="mt-1 block w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-gray-700 sm:text-sm">
              {fps} fps (Speedタブと同期)
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-4">
              <button
                onClick={() => handleAnalysis('contact')}
                className={cn(
                  "btn btn-primary h-24 w-full",
                  analysisType === 'contact' && "bg-green-600 hover:bg-green-700"
                )}
                disabled={startFrame === null || endFrame === null}
              >
                <Timer className="w-6 h-6 mr-2" />
                接地時間分析
              </button>
              {contactTime !== null && (
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-sm text-green-700 font-medium">接地時間</div>
                  <div className="text-2xl font-bold text-green-900">
                    {contactTime.toFixed(3)} 秒
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <button
                onClick={() => handleAnalysis('flight')}
                className={cn(
                  "btn btn-primary h-24 w-full",
                  analysisType === 'flight' && "bg-blue-600 hover:bg-blue-700"
                )}
                disabled={startFrame === null || endFrame === null}
              >
                <Timer className="w-6 h-6 mr-2" />
                滞空時間分析
              </button>
              {flightTime !== null && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-sm text-blue-700 font-medium">滞空時間</div>
                  <div className="text-2xl font-bold text-blue-900">
                    {flightTime.toFixed(3)} 秒
                  </div>
                </div>
              )}
            </div>
          </div>

          {feedbackMessages.length > 0 && (
            <div className="bg-indigo-50 p-4 rounded-lg">
              <div className="text-sm text-indigo-700 font-medium">
                フィードバック
              </div>
              {feedbackMessages.map((message, index) => (
                <div key={index} className="text-lg font-medium text-indigo-900 mt-2">
                  {message}
                </div>
              ))}
            </div>
          )}

          <div className="bg-blue-50 p-4 rounded-lg text-sm text-blue-700">
            <h4 className="font-medium mb-2">操作手順</h4>
            <ol className="list-decimal list-inside space-y-1">
              <li>測定したい局面を「開始」ボタンと「終了」ボタンで選択</li>
              <li>「接地時間分析」または「滞空時間分析」ボタンを押す</li>
              <li>フレーム数を確認し、計算された時間を確認</li>
            </ol>
            <p className="mt-2 text-xs">※データがおかしい場合はフレームレートを60に戻し、最初からやり直してください。</p>
          </div>
        </div>
      </div>
      <BackToDashboard />
    </div>
  );
};

export default ContactPage;